/* ==========================================================================
   App store.

   A small pub/sub cache so that, e.g., the Dashboard and the Fund Wallet
   flow both reflect the same wallet balance without either one directly
   reaching into the other's DOM. Not a framework — just enough structure
   to keep service-fetched data in one place. Every field here is data that
   came from (or will come from) a service call; nothing here is invented
   in the UI layer.
   ========================================================================== */

import type { Bill, Meter, MeterReading, Transaction, WalletBalance } from "../types";

export interface AppState {
  wallet: WalletBalance | null;
  meters: Meter[];
  readings: Record<string, MeterReading>; // keyed by meterId
  recentTransactions: Transaction[];
  bills: Bill[];
  loading: {
    wallet: boolean;
    meters: boolean;
    transactions: boolean;
    bills: boolean;
  };
}

type Listener = (state: AppState) => void;

const state: AppState = {
  wallet: null,
  meters: [],
  readings: {},
  recentTransactions: [],
  bills: [],
  loading: { wallet: false, meters: false, transactions: false, bills: false },
};

const listeners = new Set<Listener>();

function notify(): void {
  listeners.forEach((fn) => fn(state));
}

export const appStore = {
  get state(): Readonly<AppState> {
    return state;
  },

  subscribe(fn: Listener): () => void {
    listeners.add(fn);
    fn(state);
    return () => listeners.delete(fn);
  },

  setWallet(wallet: WalletBalance): void {
    state.wallet = wallet;
    notify();
  },

  setMeters(meters: Meter[]): void {
    state.meters = meters;
    notify();
  },

  setReading(reading: MeterReading): void {
    state.readings[reading.meterId] = reading;
    notify();
  },

  setRecentTransactions(items: Transaction[]): void {
    state.recentTransactions = items;
    notify();
  },

  prependTransaction(tx: Transaction): void {
    state.recentTransactions = [tx, ...state.recentTransactions].slice(0, 20);
    notify();
  },

  setBills(bills: Bill[]): void {
    state.bills = bills;
    notify();
  },

  addBill(bill: Bill): void {
    state.bills = [bill, ...state.bills];
    notify();
  },

  updateBill(bill: Bill): void {
    state.bills = state.bills.map((b) => (b.id === bill.id ? bill : b));
    notify();
  },

  setLoading(key: keyof AppState["loading"], value: boolean): void {
    state.loading[key] = value;
    notify();
  },

  /** Wipe every cached field — called on logout so the next login never
   *  shows a flash of the previous user's data. */
  reset(): void {
    state.wallet = null;
    state.meters = [];
    state.readings = {};
    state.recentTransactions = [];
    state.bills = [];
    state.loading = { wallet: false, meters: false, transactions: false, bills: false };
    notify();
  },
};
